import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowLeft, Badge, Button, Modal, Plus, Select, Tabs } from '@opendate-app/setlist';
import { useConductorCues, useOpendateCues, usePastCues } from '../hooks/useConductorCues';
import '../styles/conductorManualPanel.css';
import '../styles/conductorCuesPanel.css';

const TABS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'past', label: 'Past' },
  { value: 'opendate', label: 'From Opendate' }
];

const REPEAT_LABELS = {
  once: 'Once',
  daily: 'Every day',
  weekdays: 'Every weekday',
  weekly: 'Every week'
};

const DELIVERY_LABELS = {
  in_app: 'In Conductor',
  text: 'By text',
  group_text: 'In a group text'
};

const deliveryOptionsFor = (cue, deliveryOptions) => {
  if (deliveryOptions.some((option) => option.value === cue.delivery_target)) return deliveryOptions;

  return [{ value: cue.delivery_target, label: DELIVERY_LABELS[cue.delivery] || cue.delivery }, ...deliveryOptions];
};

const OUTCOMES = {
  delivered: { label: 'Sent', color: 'success' },
  nothing_to_report: { label: 'Nothing to report', color: 'gray' }
};

const PanelStatus = ({ loading, error, onRetry, loadingText, errorText }) => {
  if (loading) return <div className="venue-conductor-manual-panel-status">{loadingText}</div>;
  if (!error) return null;

  return (
    <div className="venue-conductor-manual-panel-status">
      <p>{errorText}</p>
      <Button variant="secondary" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
};

PanelStatus.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.bool,
  onRetry: PropTypes.func.isRequired,
  loadingText: PropTypes.string.isRequired,
  errorText: PropTypes.string.isRequired
};

const ScheduledCues = ({ onNewCue, onOpenThread }) => {
  const { cues, deliveryOptions, emailOptions, updatingCueIds, loading, error, reload, cancelCue, changeDelivery, changeEmailRecipients } = useConductorCues();
  const [cueToCancel, setCueToCancel] = useState(null);

  if (loading || error) {
    return <PanelStatus loading={loading} error={error} onRetry={reload} loadingText="Loading your cues…" errorText="Couldn't load your cues." />;
  }
  if (!cues) return null;

  if (cues.length === 0) {
    return (
      <div className="venue-conductor-manual-panel-status">
        <p>You don&apos;t have any cues scheduled.</p>
        <p>A cue is something Conductor does for you later, once or on repeat: a reminder at 4, a sales recap every Monday, or a heads-up when a show is selling slowly.</p>
        <Button variant="primary" size="sm" icon={Plus} onClick={onNewCue}>
          New cue
        </Button>
      </div>
    );
  }

  return (
    <>
      <ul className="venue-conductor-cues" aria-label="Scheduled cues">
        {cues.map((cue) => (
          <li key={cue.id} className="venue-conductor-cue">
            <div className="venue-conductor-cue-title">{cue.title}</div>
            <div className="venue-conductor-cue-meta">
              <Badge size="sm" color="brand">{REPEAT_LABELS[cue.repeat] || cue.repeat}</Badge>
              <span>Next: {cue.next_run_label}</span>
            </div>
            <div className="venue-conductor-cue-delivery">
              <Select
                size="sm"
                label="Where it runs"
                name={`cue-${cue.id}-delivery`}
                width={240}
                options={deliveryOptionsFor(cue, deliveryOptions)}
                value={cue.delivery_target}
                disabled={updatingCueIds.includes(cue.id)}
                onChange={(deliveryTarget) => {
                  if (deliveryTarget !== cue.delivery_target) changeDelivery(cue, deliveryTarget);
                }}
              />
              <Select
                size="sm"
                multiple
                label="Also email to"
                name={`cue-${cue.id}-email`}
                width={240}
                options={emailOptions}
                value={cue.email_team_membership_ids}
                disabled={updatingCueIds.includes(cue.id)}
                onChange={(ids) => changeEmailRecipients(cue, ids)}
              />
            </div>
            <div className="venue-conductor-cue-actions">
              <Button variant="secondary" size="xs" onClick={() => onOpenThread(cue.ai_thread_id)}>
                Open conversation
              </Button>
              <Button variant="secondary" size="xs" onClick={() => setCueToCancel(cue)}>
                Cancel cue
              </Button>
            </div>
          </li>
        ))}
      </ul>
      <Modal
        open={Boolean(cueToCancel)}
        type="error"
        size="sm"
        title="Cancel this cue?"
        description={cueToCancel ? `"${cueToCancel.title}" won't run again. Its past runs stay under Past.` : null}
        confirmLabel="Cancel cue"
        cancelLabel="Keep cue"
        onConfirm={() => {
          cancelCue(cueToCancel);
          setCueToCancel(null);
        }}
        onCancel={() => setCueToCancel(null)}
        onClose={() => setCueToCancel(null)}
      />
    </>
  );
};

ScheduledCues.propTypes = {
  onNewCue: PropTypes.func.isRequired,
  onOpenThread: PropTypes.func.isRequired
};

const PastCues = ({ onOpenThread }) => {
  const { runs, loading, error, reload } = usePastCues();

  if (loading || error) {
    return <PanelStatus loading={loading} error={error} onRetry={reload} loadingText="Loading your past cues…" errorText="Couldn't load your past cues." />;
  }
  if (!runs) return null;

  if (runs.length === 0) {
    return (
      <div className="venue-conductor-manual-panel-status">
        <p>No cues have run yet.</p>
        <p>Each time one of your cues runs, it shows up here with what Conductor sent.</p>
      </div>
    );
  }

  return (
    <ul className="venue-conductor-cues" aria-label="Past cues">
      {runs.map((run) => {
        const outcome = OUTCOMES[run.outcome] || { label: run.outcome, color: 'gray' };
        return (
          <li key={run.id} className="venue-conductor-cue">
            <div className="venue-conductor-cue-title">{run.title}</div>
            <div className="venue-conductor-cue-meta">
              <Badge size="sm" color={outcome.color}>{outcome.label}</Badge>
              <span>{run.ran_at_label}</span>
              <span>{DELIVERY_LABELS[run.delivery] || run.delivery}</span>
            </div>
            {run.summary && <p className="venue-conductor-cue-body">{run.summary}</p>}
            <div className="venue-conductor-cue-actions">
              <Button variant="secondary" size="xs" onClick={() => onOpenThread(run.ai_thread_id)}>
                Open conversation
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

PastCues.propTypes = {
  onOpenThread: PropTypes.func.isRequired
};

const FromOpendate = ({ onAskConductor }) => {
  const { insights, loading, error, reload, dismissInsight } = useOpendateCues();

  if (loading || error) {
    return <PanelStatus loading={loading} error={error} onRetry={reload} loadingText="Loading cues from Opendate…" errorText="Couldn't load cues from Opendate." />;
  }
  if (!insights) return null;

  if (insights.length === 0) {
    return (
      <div className="venue-conductor-manual-panel-status">
        <p>Nothing from Opendate right now.</p>
        <p>Opendate cues you when something needs a look, like holds nobody has touched in a while.</p>
      </div>
    );
  }

  return (
    <ul className="venue-conductor-cues" aria-label="Cues from Opendate">
      {insights.map((insight) => (
        <li key={insight.id} className="venue-conductor-cue">
          <div className="venue-conductor-cue-title">{insight.title}</div>
          {insight.body && <p className="venue-conductor-cue-body">{insight.body}</p>}
          <div className="venue-conductor-cue-actions">
            {insight.prompt && (
              <Button variant="secondary" size="xs" onClick={() => onAskConductor(insight.prompt)}>
                Look into this
              </Button>
            )}
            <Button variant="secondary" size="xs" onClick={() => dismissInsight(insight)}>
              Dismiss
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
};

FromOpendate.propTypes = {
  onAskConductor: PropTypes.func.isRequired
};

const ConductorCuesPanel = ({ onClose, onNewCue, onOpenThread, onAskConductor, initialView = 'scheduled' }) => {
  const [view, setView] = useState(initialView);

  return (
    <aside role="dialog" aria-label="Cues" className="venue-conductor-manual-panel">
      <header className="venue-conductor-ui-header venue-conductor-manual-panel-header">
        <div className="venue-conductor-ui-header-group venue-conductor-motion-stage" style={{ '--motion-stage': 1 }}>
          <Button
            variant="tertiary"
            size="sm"
            icon={ArrowLeft}
            className="venue-conductor-ui-chat-menu"
            aria-label="Back to chat"
            onClick={onClose}
          >
            Back to chat
          </Button>
          <span className="venue-conductor-manual-panel-title">Cues</span>
        </div>
        <div className="venue-conductor-ui-header-group venue-conductor-motion-stage" style={{ '--motion-stage': 2 }}>
          <Button variant="secondary" size="sm" icon={Plus} onClick={onNewCue}>
            New cue
          </Button>
        </div>
      </header>
      <div className="venue-conductor-cues-tabs">
        <Tabs
          aria-label="Cues sections"
          variant="button-gray"
          size="sm"
          items={TABS}
          value={view}
          onChange={setView}
          collapseBelow={0}
        />
      </div>
      <div className="venue-conductor-manual-panel-body">
        {view === 'scheduled' && <ScheduledCues onNewCue={onNewCue} onOpenThread={onOpenThread} />}
        {view === 'past' && <PastCues onOpenThread={onOpenThread} />}
        {view === 'opendate' && <FromOpendate onAskConductor={onAskConductor} />}
      </div>
    </aside>
  );
};

ConductorCuesPanel.propTypes = {
  onClose: PropTypes.func.isRequired,
  onNewCue: PropTypes.func.isRequired,
  onOpenThread: PropTypes.func.isRequired,
  onAskConductor: PropTypes.func.isRequired,
  initialView: PropTypes.oneOf(['scheduled', 'past', 'opendate'])
};

export default ConductorCuesPanel;
